/**
 * @voltxampere/engine — `.NODESET`-style DC initial conditions (Sprint 8c)
 *
 * Sprint 8b.1's PNP CE diagnostic (see
 * `docs/sprints/diagnostics/sprint-8c-pnp-diagnostic.md` Tests 3 / 4)
 * proved that cold-start Newton — even with Armijo — is trapped at
 * the trivial BJT-off fixed point (V=0 everywhere, KCL identically
 * satisfied). ngspice escapes this via `.NODESET V(node)=...`
 * directives that seed Newton's initial iterate inside the target
 * basin of attraction. Sprint 8c ports the same mechanism to v3.
 *
 * Two hint layers:
 *
 * 1. **Node voltage hints** (`hints.nodes[netId] = voltage`).
 *    Projected directly into the initial iterate at
 *    `x[plan.netToIndex.get(netId)]`. Ground nets and
 *    non-resolvable nets are silently ignored (the caller's fault,
 *    but silent-ignore matches ngspice's behaviour).
 *
 * 2. **Component-level junction hints** (`hints.components[id] =
 *    { vbe, vbc, vce, … }`). For BJTs, these project into both the
 *    initial iterate AND the Newton driver's pnjlim junction
 *    history so the first Ebers-Moll evaluation sits near the
 *    intended junction voltage. Sign conventions:
 *
 *    - `vbe` hint is in **NPN convention**: `vbe = V_B − V_E`.
 *      For PNP active region the user writes a negative number
 *      (same sign as the physical node difference, not the stamp's
 *      internal post-sign-flip variable).
 *    - Ditto `vbc = V_B − V_C`.
 *    - `vce = V_C − V_E` (convenience for sat/active boundary
 *      hints; used only when `vbc` is absent).
 *
 *    When pre-seeding pnjlim, the NPN-convention hint is flipped
 *    by the polarity sign so it matches the stamp's internal
 *    `vbeRaw = sign · (V_B − V_E)` — without this flip the first
 *    Ebers-Moll evaluation would walk the PNP's junction in the
 *    wrong direction and push the iterate right back to the BJT-
 *    off trap.
 *
 * All hints are **tavsiye** (advisory): Newton still believes the
 * stamps' Kirchhoff balance and self-corrects via Armijo
 * backtracking if the hint is wrong. This is deliberate — a
 * hard-coded hint override would masquerade as convergence even
 * when the circuit physics dictate otherwise.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { bjtParasiticSlots } from "../stamps/nonlinear/bjt.js";
/**
 * Translate a DC initial-conditions schema into a concrete seeded
 * iterate + pnjlim history. Returns a zero-length-effect result
 * (copy of `xDefault`, empty map) when `hints` is `undefined` or
 * empty.
 */
export function applyInitialConditions(circuit, plan, hints, xDefault) {
    const x = new Float64Array(xDefault);
    const junctionHistory = new Map();
    let applied = 0;
    const ignoredNets = [];
    const ignoredComponents = [];
    if (!hints) {
        return { x, junctionHistory, applied, ignoredNets, ignoredComponents };
    }
    // Sprint 10.0 (smell #3 closure): pre-compute the legitimate id
    // sets so the loops below can distinguish "user typo" (id is not
    // anywhere in the circuit) from "legitimate ngspice-style skip"
    // (ground net, augmented row).
    const knownNetIds = new Set(circuit.nets.map((n) => n.id));
    // Pass 1: direct node voltage hints. Applied first so subsequent
    // component-level hints that reference `V_E` via the emitter net
    // see the user's override rather than the pre-default zero.
    if (hints.nodes) {
        for (const [netId, voltage] of Object.entries(hints.nodes)) {
            if (!knownNetIds.has(netId)) {
                // Sprint 10.0 (smell #3 closure): typo / dangling reference.
                // Surface to caller as an ignored entry so DCSolver can warn.
                ignoredNets.push(netId);
                continue;
            }
            const idx = plan.netToIndex.get(netId);
            if (idx === undefined)
                continue; // ground net (legitimate skip)
            if (!plan.nodeIndexSet.has(idx))
                continue; // out of node block
            x[idx] = voltage;
            applied += 1;
        }
    }
    // Pass 2: component-level hints. Currently only BJTs are
    // supported (Sprint 8c scope — the PNP CE fixture is the closing
    // requirement). MOSFET / linear-component hints are schema-level
    // no-ops and will be wired in Sprint 9.
    if (hints.components) {
        for (const [compId, chints] of Object.entries(hints.components)) {
            const comp = circuit.components.find((c) => c.id === compId);
            if (!comp) {
                // Sprint 10.0 (smell #3 closure): typo / dangling reference.
                ignoredComponents.push(compId);
                continue;
            }
            if (comp.type === "bjt-npn" || comp.type === "bjt-pnp") {
                applied += applyBJTHint(comp, comp.type, chints, plan, x, junctionHistory);
            }
            // MOSFET / voltage / vgs / vds hints: deferred to Sprint 9.
            // The Shichman-Hodges stamp does not rely on pnjlim so a
            // junction-history seeding is not meaningful; a direct
            // Vgs/Vds offset on the node hint layer would be the correct
            // place to handle them.
        }
    }
    return { x, junctionHistory, applied, ignoredNets, ignoredComponents };
}
function applyBJTHint(comp, polarity, hints, plan, x, junctionHistory) {
    const baseNet = comp.terminals.find((t) => t.terminalId === "base")?.netId;
    const collectorNet = comp.terminals.find((t) => t.terminalId === "collector")?.netId;
    const emitterNet = comp.terminals.find((t) => t.terminalId === "emitter")?.netId;
    if (baseNet === undefined || collectorNet === undefined || emitterNet === undefined) {
        return 0;
    }
    // `netToIndex` returns `undefined` for the ground net — that's a
    // real index (virtually the "always zero" row) rather than a
    // validation failure. The emitter-at-ground case is common (PNP
    // CE fixture has V_E = −1.971 V but a well-tied NPN sometimes
    // lands E at GND). Treat undefined as ground = 0 V.
    const bIdx = plan.netToIndex.get(baseNet);
    const cIdx = plan.netToIndex.get(collectorNet);
    const eIdx = plan.netToIndex.get(emitterNet);
    const vE = eIdx !== undefined ? (x[eIdx] ?? 0) : 0;
    // NPN convention sign flip: stamp internally stores
    // `vbeRaw = sign · (V_B − V_E)` where `sign = -1` for PNP. The
    // user hint is in unflipped (physical node-difference) units, so
    // we multiply by the polarity sign before writing pnjlim
    // history.
    const polaritySign = polarity === "bjt-npn" ? 1 : -1;
    let applied = 0;
    if (hints.vbe !== undefined && bIdx !== undefined) {
        x[bIdx] = vE + hints.vbe;
        junctionHistory.set(`${comp.id}:vbe`, polaritySign * hints.vbe);
        applied += 1;
    }
    if (hints.vbc !== undefined && cIdx !== undefined) {
        const vB = bIdx !== undefined ? (x[bIdx] ?? 0) : 0;
        x[cIdx] = vB - hints.vbc;
        junctionHistory.set(`${comp.id}:vbc`, polaritySign * hints.vbc);
        applied += 1;
    }
    else if (hints.vce !== undefined && cIdx !== undefined) {
        // Convenience: `vce = V_C − V_E`. Only used when `vbc` is
        // absent (explicit vbc wins). When both `vbe` and `vce` are
        // present we can derive `vbc = vbe − vce` and pre-seed the BC
        // junction too — otherwise the BC pnjlim starts from zero,
        // which is reverse-biased for NPN active / PNP active.
        x[cIdx] = vE + hints.vce;
        applied += 1;
        if (hints.vbe !== undefined) {
            const vbcDerived = hints.vbe - hints.vce;
            junctionHistory.set(`${comp.id}:vbc`, polaritySign * vbcDerived);
        }
    }
    // Sprint 26 Lane 17 — parazitik internal-node IC mirror.
    //
    // The Sprint 24 B1 full SPICE3 bjtload pattern allocates internal
    // nodes (B'/C'/E') between the external pin and the Ebers-Moll
    // junction whenever Rb / Rc / Re is non-zero. The Sprint 24 B1
    // PNP carry-over (see deferred test in
    // `tests/regression/sprint-24-bjt-parazitik.test.ts` tail comment)
    // diagnosed that `applyInitialConditions` seeded only the EXTERNAL
    // pins; the internal nodes stayed at the `xDefault` zero. For PNP
    // active region the resulting ΔV across the parasitic resistor
    // (e.g. V_E ≈ −1.5 V, V_E' = 0 V across Re=1 Ω) injects a transient
    // I = ΔV/R ≈ 1.5 A on Newton iter 1 and pushes the iterate outside
    // the basin Armijo can recover.
    //
    // Mirror the IC-seeded external pin voltage onto each allocated
    // internal node so the first iterate carries zero ΔV across every
    // parazitik resistor — Newton's first Ebers-Moll evaluation then
    // sees the same junction voltages as the IC seed intended.
    // Polarity-invariant: the parazitik resistor doesn't care about
    // sign convention, so the same mirror logic applies to NPN and PNP
    // (Sprint 24 B1 NPN tests don't supply IC, so this code path is
    // dormant for them — engine zero-diff invariant for-existing-cases
    // verified by post-change sentinel run).
    const internalIndices = plan.internalNodeIndices.get(comp.id);
    if (internalIndices !== undefined && internalIndices.length > 0) {
        const slots = bjtParasiticSlots(comp);
        for (let i = 0; i < slots.length; i++) {
            const slot = slots[i];
            const internalIdx = internalIndices[i];
            if (slot === undefined || internalIdx === undefined)
                continue;
            const externalIdx = slot.kind === "Rb" ? bIdx : slot.kind === "Rc" ? cIdx : eIdx;
            if (externalIdx === undefined)
                continue;
            x[internalIdx] = x[externalIdx] ?? 0;
        }
    }
    return applied;
}
//# sourceMappingURL=initial-conditions.js.map